import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { CompanyDailyNavConfigComp, CompanyDailyNavConfigVersion } from 'api';
import { firmDailyNavSettingsUrl } from 'common/config/urls';
import {
  DAILY_NAV_THRESHOLD_GROUPS,
  DAILY_NAV_THRESHOLD_KEYS,
  DailyNavThresholdKey,
  fractionToPercent,
  isValidThresholdPercent,
  percentToFraction,
} from 'common/constants/dailyNav';
import { useStore } from 'common/store';
import { CurrentCompanyMeasurementDate, SelectedMeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import AlexandriaCompanyPicker from 'components/AlexandriaCompanyPicker';
import { GridSkeleton } from 'components/Grid';
import AddGpcDialog from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog';
import { CapIQ, CapIQCompanyData } from 'pages/ValuationsAllocation/types';
import { useReadUserAccessLevel } from 'services/hooks';
import {
  useGetCompanyDailyNavConfig,
  useGetCompanyDailyNavConfigHistory,
  useGetCompanyGpcApproaches,
  useUpdateCompanyDailyNavConfig,
} from 'services/hooks/dailyNav';
import { ADD_COMP_OR_ETF_BUTTON_LABEL, ADD_PUBLIC_OR_ETF_DIALOG_TITLE } from './constants';
import { DAILY_MONITORING_TEST_IDS } from './DailyMonitoring.testIds';

// Overrides are kept as strings: '' means "inherited from the firm default",
// while any other value — including '0' — is an explicit override.
type OverridesState = Record<DailyNavThresholdKey, string>;

const EMPTY_OVERRIDES = Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, ''])) as OverridesState;

const formatEffectivePeriod = (version: { effective_from: string; effective_to?: string | null }) => {
  const from = format(new Date(version.effective_from), 'MMM d, yyyy h:mm a');
  const to = version.effective_to ? format(new Date(version.effective_to), 'MMM d, yyyy h:mm a') : 'Present';
  return `${from} — ${to}`;
};

const CHANGE_TRIGGER_LABELS: Record<CompanyDailyNavConfigVersion.change_trigger, string> = {
  [CompanyDailyNavConfigVersion.change_trigger.FIRM_TEMPLATE_CHANGE]: 'Firm template change',
  [CompanyDailyNavConfigVersion.change_trigger.COMPANY_EDIT]: 'Company edit',
};

// Draft shape for the comps watchlist. The API's readonly CompanyDailyNavConfigComp adds
// an `order` field we don't need locally; the write payload matches this shape exactly.
type CompRow = {
  comp_type: CompanyDailyNavConfigComp.comp_type;
  cap_iq_id: string;
  ticker_symbol: string;
  company_name: string;
};

const COMP_TYPE_LABELS: Record<CompanyDailyNavConfigComp.comp_type, string> = {
  [CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP]: 'Public Comp',
  [CompanyDailyNavConfigComp.comp_type.ETF]: 'ETF',
};

const toCompRow = (comp: {
  comp_type: CompanyDailyNavConfigComp.comp_type;
  cap_iq_id: string;
  ticker_symbol: string;
  company_name: string;
}): CompRow => ({
  comp_type: comp.comp_type,
  cap_iq_id: comp.cap_iq_id,
  ticker_symbol: comp.ticker_symbol,
  company_name: comp.company_name,
});

// Cap IQ fields come wrapped in a { value } envelope; extract to a plain string.
const capIqFieldToString = (field?: CapIQ): string =>
  field?.value === null || field?.value === undefined ? '' : String(field.value);

// ticker_symbol values from Cap IQ come as "Exchange:Symbol" (e.g. "NASDAQ:AAPL");
// strip the exchange prefix so we store just the symbol.
const stripExchangePrefix = (ticker: string): string =>
  ticker.includes(':') ? (ticker.split(':').pop() ?? ticker) : ticker;

// Cap IQ classifies ETFs and index funds as "public fund" / "index"; everything
// else searched via the comparable-company picker is treated as a public comp.
const ETF_COMPANY_TYPES = ['public fund', 'index'];
const inferCompType = (companyType: string): CompanyDailyNavConfigComp.comp_type =>
  ETF_COMPANY_TYPES.includes(companyType.trim().toLowerCase())
    ? CompanyDailyNavConfigComp.comp_type.ETF
    : CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP;

// Reported up to CompanySummary so the Save action can be rendered in the tertiary
// nav instead of inline. CompanySummary owns the single tertiary-nav registration
// slot, so DailyMonitoring must never register its own tertiary nav state.
export interface DailyMonitoringSaveAction {
  disabled: boolean;
  isSaving: boolean;
  isVisible: boolean;
  onSave: () => void;
}

type DailyMonitoringProps = {
  // The measurement date selected via useCompanyMeasurementDateFilter (threaded
  // from CompanySummary). Its cmd_id scopes the GPC-approaches lookup; its id
  // (the MeasurementDate pk) is what the Cap IQ pc-list endpoint expects.
  selectedMeasurementDate?: SelectedMeasurementDate;
  // Reports the current Save action (or null when it should be hidden) up to
  // CompanySummary, which renders it via the tertiary nav registration it owns.
  onSaveActionChange?: (action: DailyMonitoringSaveAction | null) => void;
};

const DailyMonitoring = ({ selectedMeasurementDate, onSaveActionChange }: DailyMonitoringProps) => {
  const [{ companyInfo, firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const firmId = firmInfo?.id ?? null;
  const companyId = companyInfo?.id ?? null;

  const { isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const { data: currentConfig, isLoading } = useGetCompanyDailyNavConfig(firmId, companyId);
  const { history, loadMoreHistory, hasMoreHistory, isLoadingMoreHistory } = useGetCompanyDailyNavConfigHistory(
    firmId,
    companyId
  );
  const { updateCompanyDailyNavConfig, isPending } = useUpdateCompanyDailyNavConfig();
  const { approaches: gpcApproaches } = useGetCompanyGpcApproaches(selectedMeasurementDate?.cmd_id);

  const [overrides, setOverrides] = useState<OverridesState>(EMPTY_OVERRIDES);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [comps, setComps] = useState<CompRow[]>([]);
  const [selectedGpcApproachId, setSelectedGpcApproachId] = useState<number | ''>('');
  const [alexandriaId, setAlexandriaId] = useState<string | null>(null);

  const firmConfig = currentConfig?.firm_config ?? null;
  const companyConfig = currentConfig?.company_config ?? null;
  // Company overrides anchor to the firm's open config version; until the firm
  // has saved a template at least once, there is nothing to anchor to.
  const hasSavedFirmConfig = Boolean(firmConfig?.id);
  const canEdit = isAdminOrStaffOrSuperuser && hasSavedFirmConfig;

  useEffect(() => {
    if (currentConfig) {
      const companyValues = currentConfig.company_config;
      setOverrides(
        Object.fromEntries(
          // Null from the API means "inherited"; 0 is a valid explicit override
          DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(companyValues?.[key])])
        ) as OverridesState
      );
      setComps((companyValues?.comps ?? []).map(toCompRow));
      setAlexandriaId(companyValues?.alexandria_id ?? null);
    }
  }, [currentConfig]);

  const isFormValid = useMemo(
    () => DAILY_NAV_THRESHOLD_KEYS.every(key => overrides[key] === '' || isValidThresholdPercent(overrides[key])),
    [overrides]
  );

  const isCompsDirty = useMemo(() => {
    const savedComps = companyConfig?.comps ?? [];
    if (savedComps.length !== comps.length) {
      return true;
    }
    return savedComps.some((savedComp, index) => {
      const draftComp = comps[index];
      return (
        !draftComp ||
        draftComp.comp_type !== savedComp.comp_type ||
        draftComp.cap_iq_id !== savedComp.cap_iq_id ||
        draftComp.ticker_symbol !== savedComp.ticker_symbol ||
        draftComp.company_name !== savedComp.company_name
      );
    });
  }, [companyConfig, comps]);

  const isAlexandriaDirty = (companyConfig?.alexandria_id ?? null) !== alexandriaId;

  const isDirty = useMemo(
    () =>
      DAILY_NAV_THRESHOLD_KEYS.some(key => fractionToPercent(companyConfig?.[key]) !== overrides[key]) ||
      isCompsDirty ||
      isAlexandriaDirty,
    [companyConfig, overrides, isCompsDirty, isAlexandriaDirty]
  );

  const saveDailyMonitoringConfig = useCallback(async () => {
    if (!firmId || !companyId || !isFormValid || !hasSavedFirmConfig) {
      return;
    }
    const toPayloadValue = (key: DailyNavThresholdKey) =>
      overrides[key] === '' ? null : percentToFraction(overrides[key]);
    const updatedConfig = await updateCompanyDailyNavConfig(firmId, companyId, {
      cap_iq_one_day_delta: toPayloadValue('cap_iq_one_day_delta'),
      cap_iq_five_day_delta: toPayloadValue('cap_iq_five_day_delta'),
      cap_iq_since_last_valuation_delta: toPayloadValue('cap_iq_since_last_valuation_delta'),
      secondary_one_day_delta: toPayloadValue('secondary_one_day_delta'),
      secondary_five_day_delta: toPayloadValue('secondary_five_day_delta'),
      secondary_since_last_valuation_delta: toPayloadValue('secondary_since_last_valuation_delta'),
      comps,
      alexandria_id: alexandriaId,
    });
    if (updatedConfig) {
      enqueueSnackbar('Daily NAV Settings configuration successfully saved', { variant: 'success' });
    }
  }, [
    firmId,
    companyId,
    isFormValid,
    hasSavedFirmConfig,
    overrides,
    comps,
    alexandriaId,
    updateCompanyDailyNavConfig,
    enqueueSnackbar,
  ]);

  // saveDailyMonitoringConfig's identity changes whenever overrides/comps change; wrap it in a
  // stable callback (backed by a ref) so registering it with onSaveActionChange doesn't churn.
  const saveDailyMonitoringConfigRef = useRef(saveDailyMonitoringConfig);
  useEffect(() => {
    saveDailyMonitoringConfigRef.current = saveDailyMonitoringConfig;
  }, [saveDailyMonitoringConfig]);
  const stableSave = useCallback(() => saveDailyMonitoringConfigRef.current(), []);

  const saveActionDisabled = !firmId || !companyId || !isFormValid || !isDirty || !hasSavedFirmConfig || isPending;

  // Report the Save action up to CompanySummary, which owns the single tertiary-nav
  // registration slot. Do not register tertiary nav state directly from this component.
  useEffect(() => {
    onSaveActionChange?.({
      disabled: saveActionDisabled,
      isSaving: isPending,
      isVisible: isAdminOrStaffOrSuperuser,
      onSave: stableSave,
    });
    return () => onSaveActionChange?.(null);
  }, [saveActionDisabled, isPending, isAdminOrStaffOrSuperuser, stableSave, onSaveActionChange]);

  const selectedVersion = useMemo(
    () => history?.find(version => version.id === selectedVersionId) ?? null,
    [history, selectedVersionId]
  );

  // Company Summary isn't wrapped in ValuationContext, so AddGpcDialog can't
  // read the page's date from context and would otherwise fall back to the
  // firm's oldest measurement date. Build the same CompanyMeasurementDate shape
  // ValuationsAllocation derives from useCompanyMeasurementDateFilter: `id` is
  // the CompanyMeasurementDate pk and `md_id` the MeasurementDate pk (the filter
  // exposes those as cmd_id and id respectively). Paired with currentMarketDate
  // below, the Cap IQ search resolves against the selected date rather than the
  // oldest firm date.
  const gpcDialogMeasurementDate = useMemo<CurrentCompanyMeasurementDate | undefined>(() => {
    if (!selectedMeasurementDate) {
      return undefined;
    }
    return {
      id: selectedMeasurementDate.cmd_id ?? null,
      name: selectedMeasurementDate.name,
      slug: selectedMeasurementDate.slug ?? '',
      date: selectedMeasurementDate.date,
      is_open: selectedMeasurementDate.is_open ?? false,
      md_id: selectedMeasurementDate.id ?? null,
      company_id: selectedMeasurementDate.company_id ?? companyId ?? 0,
      company_name: companyInfo?.name ?? '',
      is_ready: selectedMeasurementDate.is_ready ?? false,
    };
  }, [selectedMeasurementDate, companyId, companyInfo?.name]);

  const handleOverrideChange = (key: DailyNavThresholdKey, value: string) => {
    setOverrides(prevState => ({ ...prevState, [key]: value }));
  };

  const handleResetToFirmDefault = (key: DailyNavThresholdKey) => {
    setOverrides(prevState => ({ ...prevState, [key]: '' }));
  };

  // Replaces the current comps list wholesale. This is a one-time copy — there's no
  // live link back to the GPC approach, so later GPC changes won't reflect here.
  const handleCopyFromGpcApproach = () => {
    const approach = gpcApproaches?.find(candidate => candidate.gpc_approach_id === selectedGpcApproachId);
    if (!approach) {
      return;
    }
    const copiedComps: CompRow[] = approach.comps
      .filter(comp => Boolean(comp.cap_iq_id))
      .map(comp => ({
        comp_type: CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP,
        cap_iq_id: comp.cap_iq_id as string,
        ticker_symbol: comp.ticker_symbol ?? '',
        company_name: comp.company_name ?? '',
      }));
    setComps(copiedComps);
  };

  // AddGpcDialog is a pure picker: it searches Cap IQ (read-only) and hands back the
  // selected comparable companies via onAdd. We snapshot each one's identity into the
  // local watchlist, inferring ETF vs public comp from its Cap IQ company_type, and
  // skip any already present (by cap_iq_id).
  const handleAddCompsFromDialog = (data: CapIQCompanyData[]) => {
    setComps(prevState => {
      const seenIds = new Set(prevState.map(comp => comp.cap_iq_id));
      const additions: CompRow[] = [];
      data.forEach(item => {
        const capIqId = capIqFieldToString(item.gpc?.cap_iq_id);
        if (!capIqId || seenIds.has(capIqId)) {
          return;
        }
        seenIds.add(capIqId);
        additions.push({
          comp_type: inferCompType(capIqFieldToString(item.gpc?.company_type)),
          cap_iq_id: capIqId,
          ticker_symbol: stripExchangePrefix(capIqFieldToString(item.gpc?.ticker_symbol)),
          company_name: capIqFieldToString(item.gpc?.company_name),
        });
      });
      return [...prevState, ...additions];
    });
  };

  const handleRemoveComp = (capIqId: string) => {
    setComps(prevState => prevState.filter(comp => comp.cap_iq_id !== capIqId));
  };

  if (isLoading) {
    return <GridSkeleton />;
  }

  return (
    <Grid container spacing={4} sx={{ mb: 10, flexDirection: { xs: 'column', lg: 'row' } }}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <Typography variant="h6" gutterBottom>
          Daily NAV Settings
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Alert thresholds for this company. Fields left as inherited use the firm default; setting a value (including
          0%) overrides the firm default for this company only.
        </Typography>
        {!hasSavedFirmConfig && (
          <Alert severity="info" sx={{ mb: 3 }} data-testid={DAILY_MONITORING_TEST_IDS.noFirmConfigAlert}>
            This firm has not saved a Daily NAV template yet. Company-level overrides can&apos;t be configured until the
            firm saves its Daily NAV Settings.
            {isAdminOrStaffOrSuperuser && firmInfo?.slug && (
              <>
                {' '}
                <Link component={RouterLink} to={firmDailyNavSettingsUrl(firmInfo.slug)}>
                  Go to Daily NAV Settings
                </Link>
                .
              </>
            )}
          </Alert>
        )}
        {hasSavedFirmConfig && !isDirty && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}
            data-testid={DAILY_MONITORING_TEST_IDS.noChangesHint}>
            No changes to save.
          </Typography>
        )}
        {DAILY_NAV_THRESHOLD_GROUPS.map(group => (
          <Box key={group.title} sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              {group.title.toUpperCase()}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {group.fields.map(field => {
                const isOverridden = overrides[field.key] !== '';
                const firmDefault = fractionToPercent(firmConfig?.[field.key]);

                return (
                  <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      id={`daily-monitoring-threshold-${field.key}`}
                      label={field.label}
                      value={overrides[field.key]}
                      placeholder={firmDefault}
                      onChange={e => handleOverrideChange(field.key, e.target.value)}
                      error={isOverridden && !isValidThresholdPercent(overrides[field.key])}
                      disabled={!canEdit}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              %
                              {isOverridden && (
                                <Tooltip title="Reset to firm default">
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    data-testid={DAILY_MONITORING_TEST_IDS.resetToFirmDefaultButton(field.key)}
                                    onClick={() => handleResetToFirmDefault(field.key)}
                                    disabled={!canEdit}>
                                    <RestartAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        },
                        inputLabel: { shrink: true },
                      }}
                    />
                    <Chip
                      size="small"
                      sx={{ mt: 0.5 }}
                      data-testid={DAILY_MONITORING_TEST_IDS.thresholdSourceChip(field.key)}
                      label={isOverridden ? 'Override' : `Inherited (${firmDefault || '—'}%)`}
                      color={isOverridden ? 'primary' : 'default'}
                      variant={isOverridden ? 'filled' : 'outlined'}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }} data-testid={DAILY_MONITORING_TEST_IDS.alexandriaSection}>
          <Typography variant="h6" gutterBottom>
            Alexandria Company
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The Alexandria profile this company&apos;s market data is pulled from. Saving a change
            opens a new config version, so records already evaluated keep the profile they used.
          </Typography>
          <AlexandriaCompanyPicker
            firmId={firmId}
            companyId={companyId}
            selectedAlexandriaId={alexandriaId}
            onChange={setAlexandriaId}
            disabled={!canEdit}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }} data-testid={DAILY_MONITORING_TEST_IDS.compsSection}>
          <Typography variant="h6" gutterBottom>
            Comps Watchlist
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Public comps and ETFs tracked alongside this company&apos;s Daily NAV monitoring.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <Select
              size="small"
              displayEmpty
              value={selectedGpcApproachId}
              onChange={e => setSelectedGpcApproachId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={!canEdit || !gpcApproaches?.length}
              data-testid={DAILY_MONITORING_TEST_IDS.gpcApproachSelect}
              sx={{ minWidth: 260 }}>
              <MenuItem value="">
                <em>{gpcApproaches?.length ? 'Select a GPC approach' : 'No GPC approaches available'}</em>
              </MenuItem>
              {gpcApproaches?.map((approach, index) => (
                <MenuItem key={approach.gpc_approach_id} value={approach.gpc_approach_id}>
                  GPC Approach {index + 1} ({approach.comps.length} comp{approach.comps.length === 1 ? '' : 's'})
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              size="small"
              data-testid={DAILY_MONITORING_TEST_IDS.copyFromGpcButton}
              onClick={handleCopyFromGpcApproach}
              disabled={!canEdit || selectedGpcApproachId === ''}>
              Copy Comps
            </Button>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <AddGpcDialog
              buttonLabel={ADD_COMP_OR_ETF_BUTTON_LABEL}
              currentComps={comps.map(comp => ({
                cap_iq_id: comp.cap_iq_id,
                ticker_symbol: comp.ticker_symbol,
                company_name: comp.company_name,
              }))}
              dialogTitle={ADD_PUBLIC_OR_ETF_DIALOG_TITLE}
              onAdd={handleAddCompsFromDialog}
              openGpcDialog={false}
              disabled={!canEdit}
              firmId={firmId ?? undefined}
              currentMeasurementDate={gpcDialogMeasurementDate}
              currentMarketDate={selectedMeasurementDate?.date}
            />
          </Box>
          {comps.length ? (
            <Table size="small" data-testid={DAILY_MONITORING_TEST_IDS.compsTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Type</TableCell>
                  {canEdit && <TableCell align="right">Remove</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {comps.map(comp => (
                  <TableRow key={comp.cap_iq_id}>
                    <TableCell>{comp.ticker_symbol}</TableCell>
                    <TableCell>{comp.company_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={COMP_TYPE_LABELS[comp.comp_type]}
                        color={comp.comp_type === CompanyDailyNavConfigComp.comp_type.ETF ? 'secondary' : 'primary'}
                        variant="outlined"
                        data-testid={DAILY_MONITORING_TEST_IDS.compTypeChip(comp.cap_iq_id)}
                      />
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveComp(comp.cap_iq_id)}
                            data-testid={DAILY_MONITORING_TEST_IDS.removeCompButton(comp.cap_iq_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid={DAILY_MONITORING_TEST_IDS.noCompsHint}>
              No comps added yet.
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, lg: 5 }}>
        <Paper elevation={4} sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Version History
          </Typography>
          {history?.length ? (
            <List dense>
              {history.map(version => (
                <ListItemButton
                  key={version.id}
                  selected={version.id === selectedVersionId}
                  data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryItem(version.id)}
                  onClick={() => setSelectedVersionId(version.id === selectedVersionId ? null : (version.id ?? null))}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{formatEffectivePeriod(version)}</span>
                        {version.is_active && (
                          <Chip
                            size="small"
                            color="success"
                            label="Current"
                            data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryCurrentChip(version.id)}
                          />
                        )}
                      </Stack>
                    }
                    secondary={[
                      version.created_by_name ? `Changed by ${version.created_by_name}` : null,
                      CHANGE_TRIGGER_LABELS[version.change_trigger],
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No configuration changes have been saved yet.</Typography>
          )}
          {hasMoreHistory && (
            <Button
              fullWidth
              size="small"
              onClick={() => loadMoreHistory()}
              disabled={isLoadingMoreHistory}
              data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryLoadMore}>
              {isLoadingMoreHistory ? 'Loading...' : 'Load More'}
            </Button>
          )}
          {selectedVersion && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Values in effect {formatEffectivePeriod(selectedVersion)}
              </Typography>
              {selectedVersion.firm_config_version && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Anchored to the firm config effective {formatEffectivePeriod(selectedVersion.firm_config_version)}
                </Typography>
              )}
              {DAILY_NAV_THRESHOLD_GROUPS.map(group => (
                <Box key={group.title} sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {group.title.toUpperCase()}
                  </Typography>
                  {group.fields.map(field => {
                    const overrideValue = selectedVersion[field.key];
                    const firmValue = selectedVersion.firm_config_version?.[field.key];
                    const displayValue = fractionToPercent(overrideValue) || fractionToPercent(firmValue) || '—';

                    return (
                      <Typography key={field.key} variant="body2">
                        {field.label}: {displayValue}%{' '}
                        {overrideValue === null || overrideValue === undefined ? '(inherited)' : '(override)'}
                      </Typography>
                    );
                  })}
                </Box>
              ))}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  COMPS WATCHLIST
                </Typography>
                {selectedVersion.comps?.length ? (
                  <List dense data-testid={DAILY_MONITORING_TEST_IDS.historyCompsList(selectedVersion.id)}>
                    {selectedVersion.comps.map(comp => (
                      <ListItemText
                        key={comp.cap_iq_id}
                        primary={`${comp.ticker_symbol} — ${comp.company_name}`}
                        secondary={COMP_TYPE_LABELS[comp.comp_type]}
                      />
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={DAILY_MONITORING_TEST_IDS.historyNoCompsHint(selectedVersion.id)}>
                    No comps
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export { DailyMonitoring };
